/**
 * An abstract class defining a simple state machine functionality.
 *
 * The type `StateName` is defined as the union of `stateNames` values.
 * `const stateNames = ['STATE1', 'STATE2', 'STATE3'];`
 * `type StateName = * (typeof stateNames)[number]; // ('STATE1' | 'STATE2' | 'STATE3')`
 
 * `game` is a reference to the main game class.
 * The `Game` type parameter makes it more generic and prevents circular dependencies.
 */

export abstract class GameFlowBase<Game, ChangeEvent, StateName = string> {
    protected readonly stateNames: readonly StateName[] = [];
    protected readonly game!: Game;

    private readonly log: boolean = false;
    private stateIndex = 0;

    constructor(game: Game) {
        this.game = game;
    }

    nextState(): void {
        this.moveToState(this.getState(this.stateIndex + 1));
    }

    previousState(): void {
        this.moveToState(this.getState(this.stateIndex - 1));
    }

    /**
     * Sets the `stateIndex` and logs the transition.
     */
    moveToState(newState: StateName): void {
        if (newState === undefined) {
            // eslint-disable-next-line no-console
            console.error('Invalid state');
            return;
        }

        const previousState = this.getState();
        this.stateIndex = this.stateNames.indexOf(newState);

        if (this.log) {
            // eslint-disable-next-line no-console
            console.log(`Move from state: ${String(previousState)} -> ${String(this.getState())}`);
        }
    }

    getState(stateIndex = this.stateIndex): StateName {
        return this.stateNames[stateIndex];
    }

    abstract onChange(event: ChangeEvent): void;
}
